import { useQueryClient, useMutation } from '@tanstack/react-query';
import { ComponentProps, useState } from 'react';
import { DimensionValue, StyleProp, StyleSheet, ViewStyle } from 'react-native';
import Modal from 'react-native-modal';

import {
  useCampaignInfo,
  useCampaignInfoFetching,
} from './CampaignInfoProvider';
import {
  claimErrorMessages,
  ClaimErrorResponse,
  claimReward,
} from '../../api/campaigns';
import Colors from '../../constants/Colors';
import {
  AuthenticateFnParams,
  useAuthenticate,
  useLastAuthSource,
} from '../../contexts/authFlow';
import { useIsLoggedIn } from '../../contexts/authentication';
import useAppNavigation from '../../hooks/useAppNavigation';
import useColorScheme from '../../hooks/useColorScheme';
import { PrimaryButton } from '../Button';
import Loader from '../Loader';
import ModalCloseButton from '../ModalCloseButton';
import { TextBody, TextFinePrint } from '../StyledText';
import { TransparentView, View } from '../Themed';

export default function CTAButton({
  style,
  buttonColor,
  title,
  authenticateParams,
  buttonProps,
  csUuid,
}: {
  style?: StyleProp<ViewStyle>;
  buttonColor?: string;
  title?: string;
  authenticateParams?: AuthenticateFnParams;
  buttonProps?: Partial<ComponentProps<typeof PrimaryButton>>;
  csUuid?: string;
}) {
  const queryClient = useQueryClient();
  const theme = useColorScheme();

  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const hide = () => setClaimModalOpen(false);

  const authenticate = useAuthenticate();

  const isLoggedIn = useIsLoggedIn();

  const onPress = async () => {
    if (isLoggedIn) {
      setClaimModalOpen(true);
      return;
    }

    await authenticate(undefined, authenticateParams || { source: 'PAID_AD' });
    queryClient.invalidateQueries({
      queryKey: ['campaignInfo', csUuid],
    });
    setClaimModalOpen(true);
  };

  return (
    <TransparentView style={[{ alignItems: 'center' }, style]}>
      <PrimaryButton
        style={{
          backgroundColor: buttonColor || Colors[theme].accent,
          paddingHorizontal: 30,
        }}
        title={title || 'Get $50 in Child Care Credit'}
        onPress={onPress}
        {...buttonProps}
      />
      {authenticateParams?.onlyNewJoiners && (
        <TextFinePrint
          bold
          style={{ color: Colors[theme].textLowContrast, marginTop: 8 }}
        >
          *Offer only applies to new accounts created
        </TextFinePrint>
      )}

      {claimModalOpen && <ClaimModal csUuid={csUuid} hide={hide} />}
    </TransparentView>
  );
}

function useClaimRewardMutation(csUuid: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<void, ClaimErrorResponse>({
    mutationFn: async () => {
      if (!csUuid) throw new Error('csUuid is required');
      const response = await claimReward(csUuid);
      if (!response.ok) {
        throw response.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['campaignInfo', csUuid],
      });
    },
  });
}

function ClaimModal({
  csUuid,
  hide,
}: {
  csUuid: string | undefined;
  hide: () => void;
}) {
  const isCampaignInfoFetching = useCampaignInfoFetching();
  const campaignInfo = useCampaignInfo();
  const { navigate } = useAppNavigation();
  const claimMutation = useClaimRewardMutation(csUuid);

  const lastAuthSource = useLastAuthSource();

  const claimReward = () => claimMutation.mutate();

  if (!csUuid || lastAuthSource !== 'login') return null;

  const content = claimMutation.isError ? (
    <TransparentView style={{ alignItems: 'center', gap: 10 }}>
      {' '}
      {claimMutation.error.error in claimErrorMessages ? (
        <>
          <TextBody style={{ textAlign: 'center' }}>
            {claimErrorMessages[claimMutation.error.error]}
          </TextBody>
          <PrimaryButton title="OK" onPress={hide} />
        </>
      ) : (
        <>
          <TextBody>An unknown error occurred</TextBody>
          <PrimaryButton title="Try Again" onPress={claimReward} />
        </>
      )}
    </TransparentView>
  ) : claimMutation.isSuccess ? (
    <TransparentView style={{ alignItems: 'center', gap: 10 }}>
      <TextBody style={styles.tc} bold>
        Yay! 🎉
      </TextBody>
      <TextBody>You have successfully claimed your reward!</TextBody>
      <PrimaryButton
        title="Book Care and Camps Today"
        onPress={() => {
          hide();
          navigate('Explore');
        }}
      />
    </TransparentView>
  ) : isCampaignInfoFetching ? (
    <Loader />
  ) : campaignInfo?.rewardClaimed ? (
    <TextBody>You have already claimed your reward.</TextBody>
  ) : (
    <TransparentView style={{ alignItems: 'center', gap: 10 }}>
      <TextBody style={{ textAlign: 'center' }}>
        Simply click the 'Claim Reward' button, and we'll send your voucher code
        straight to your inbox!
      </TextBody>
      <PrimaryButton
        title="Claim Reward"
        onPress={claimReward}
        disabled={claimMutation.isPending}
        loading={claimMutation.isPending}
      />
    </TransparentView>
  );

  return (
    <Modal isVisible style={{ margin: 0 }}>
      <ClaimModalContainer hide={hide}>{content}</ClaimModalContainer>
    </Modal>
  );
}

function ClaimModalContainer({
  children,
  hide,
}: {
  children: React.ReactNode;
  hide: () => void;
}) {
  return (
    <TransparentView style={{ alignItems: 'center', justifyContent: 'center' }}>
      <View style={styles.alreadyLoggedInModalWrapper}>
        <ModalCloseButton absolute onPress={hide} />
        {children}
      </View>
    </TransparentView>
  );
}

const styles = StyleSheet.create({
  tc: { textAlign: 'center' },
  alreadyLoggedInModalWrapper: {
    width: 'calc(100% - 20px)' as DimensionValue,
    maxWidth: 510,
    height: '100%',
    maxHeight: 364,
    borderRadius: 30,
    padding: 40,
    paddingTop: 70,
    position: 'relative',
  },
});
